import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ButtonBase from '@mui/material/ButtonBase';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Popover from '@mui/material/Popover';
import Skeleton from '@mui/material/Skeleton';
import TextField from '@mui/material/TextField';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import clsx from 'clsx';
import { useAtom } from 'jotai';
import { useRef, useState } from 'react';

import { useTRPC } from '#src/trpc.js';
import { lastOpenProjectAtom } from '#state/atoms/last-open-project-atom.js';
import { auth0UserQueryOptions } from '#utils/auth0-user-query-options.js';
import { filterRealOrganizations } from '#utils/filter-real-organizations.js';

import type { Organization } from '@cadstrom/cadstrom-shared';

export type OrganizationSwitcherProps = {
	isLoading?: boolean | undefined;
	className?: string;
};

export function OrganizationSwitcher({ isLoading, className }: OrganizationSwitcherProps) {
	const trpc = useTRPC();
	const navigate = useNavigate();
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const [search, setSearch] = useState('');
	const [lastOpenProject, setLastOpenProject] = useAtom(lastOpenProjectAtom);
	const inputRef = useRef<HTMLInputElement>(null);

	const { data: user } = useQuery(auth0UserQueryOptions);

	const {
		data: orgsData,
		isPending,
		error,
	} = useQuery(
		trpc.organization.getMany.queryOptions(
			{},
			{
				throwOnError: false,
			},
		),
	);

	const getOrgDisplayName = (org: Organization) => org.displayName ?? org.organizationName;

	const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(e.currentTarget);
		setSearch('');
	};

	const handleClose = () => {
		setAnchorEl(null);
		setSearch('');
	};

	const handleSelect = (orgName: string) => {
		handleClose();
		setLastOpenProject({ projectId: null, revisionId: null, orgName });
		void navigate({ to: '/orgs/$orgName/projects', params: { orgName } });
	};

	if (user === null) {
		return null;
	}

	const realOrgs = filterRealOrganizations(orgsData?.organizations ?? []);

	// Single org: just display the name as a non-interactive label
	if (orgsData && realOrgs.length === 1) {
		const org = realOrgs[0];
		if (!org) {
			return null;
		}
		return (
			<span className='truncate px-2 text-xs font-semibold tracking-widest text-gray-a9 uppercase'>
				{getOrgDisplayName(org)}
			</span>
		);
	}

	const currentOrg = realOrgs.find((o) => o.organizationName === lastOpenProject.orgName);
	const displayValue =
		isPending ? null
		: error ? 'Error'
		: currentOrg ? getOrgDisplayName(currentOrg)
		: (lastOpenProject.orgName ?? 'Select org');

	const filtered = realOrgs
		.filter((o) => getOrgDisplayName(o).toLowerCase().includes(search.toLowerCase()))
		.toSorted((a, b) => getOrgDisplayName(a).localeCompare(getOrgDisplayName(b)));

	return (
		<>
			<ButtonBase
				onClick={handleOpen}
				disabled={(isLoading ?? isPending) || Boolean(error)}
				className={clsx(
					'flex w-full items-center gap-0.5 rounded px-2 py-1.5 text-left text-xs font-semibold tracking-widest text-gray-a9 uppercase transition-colors hover:bg-gray-a3 disabled:cursor-default',
					className,
				)}
			>
				<span className='truncate min-w-0 grow basis-0'>
					{displayValue ?? <Skeleton className='inline-block w-20' />}
				</span>
				<div className='flex h-0 items-center'>
					<ArrowDropDownIcon className='shrink-0 text-sm' />
				</div>
			</ButtonBase>

			<Popover
				anchorEl={anchorEl}
				open={anchorEl !== null}
				onClose={handleClose}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
				transformOrigin={{ vertical: 'top', horizontal: 'left' }}
				slotProps={{
					paper: { className: 'mt-1 flex w-56 flex-col' },
					transition: { onEntered: () => inputRef.current?.focus() },
				}}
			>
				<div className='py-1.5'>
					<p className='mb-1 p-1 px-2 text-xs font-semibold tracking-wide text-gray-a9 uppercase'>Organizations</p>
					<TextField
						inputRef={inputRef}
						placeholder='Search...'
						size='small'
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						className='w-full bg-gray-3 py-1 [&_fieldset]:border-none'
						slotProps={{ htmlInput: { className: 'py-1 text-sm' } }}
					/>
				</div>
				<MenuList dense className='max-h-80 overflow-y-auto'>
					{isPending ?
						<MenuItem disabled>
							<Skeleton width={120} />
						</MenuItem>
					: filtered.length === 0 ?
						<MenuItem disabled>
							<span>No organizations found</span>
						</MenuItem>
					:	filtered.map((org) => (
							<MenuItem
								key={org.organizationName}
								selected={org.organizationName === lastOpenProject.orgName}
								onClick={() => handleSelect(org.organizationName)}
							>
								{getOrgDisplayName(org)}
							</MenuItem>
						))
					}
				</MenuList>
			</Popover>
		</>
	);
}
